/**
 * Feature Display Name Mapping
 * Converts technical feature names to user-friendly display names
 */

export const FEATURE_DISPLAY_NAMES = {
  // Exact mappings from database (all lowercase concatenated)
  'costmanagementv2': 'Cost Management V2',
  'kpis': 'KPIs',
  'financialmanagement': 'Financial Management',
  'setup': 'Setup',
  'businessplanlist': 'Business Plan List',
  'executivedashboard': 'Executive Dashboard',
  'enhancedfinancialcalculations': 'Enhanced Financial Calculations',
  'scenariomodeling': 'Scenario Modeling',
  'costcentermanagement': 'Cost Center Management',
  'featuregovernance': 'Feature Governance',
  'financialstatements': 'Financial Statements',
  'financialplanning': 'Financial Planning',
  
  // Basic Features
  'basic_planning': 'Basic Planning',
  'single_business_plan': 'Single Business Plan',
  'standard_reports': 'Standard Reports',
  
  // Advanced Features
  'advanced_planning': 'Advanced Planning',
  'performance_tracking': 'Performance Tracking',
  'scenario_modeling': 'Scenario Modeling',
  'advanced_kpis': 'Advanced KPIs',
  
  // Business Features
  'multi_subsidiary': 'Multi-Subsidiary Management',
  'cost_centers': 'Cost Centers',
  'business_intelligence': 'Business Intelligence',
  'advanced_reports': 'Advanced Reports',
  'export': 'Export Functionality',
  
  // Enterprise Features
  'everything': 'All Features',
  'api_access': 'API Access',
  'custom_integrations': 'Custom Integrations',
  'white_labeling': 'White Labeling',
  'dedicated_support': 'Dedicated Support',
  
  // Additional Features
  'financial_modeling': 'Financial Modeling',
  'cash_flow_analysis': 'Cash Flow Analysis',
  'break_even_analysis': 'Break-Even Analysis',
  'sensitivity_analysis': 'Sensitivity Analysis',
  'market_analysis': 'Market Analysis',
  'competitor_analysis': 'Competitor Analysis',
  'swot_analysis': 'SWOT Analysis',
  'risk_assessment': 'Risk Assessment',
  'team_collaboration': 'Team Collaboration',
  'version_control': 'Version Control',
  'audit_trail': 'Audit Trail',
  'custom_templates': 'Custom Templates',
  'priority_support': 'Priority Support',
  'onboarding_training': 'Onboarding & Training',
  'scheduled_reports': 'Scheduled Reports',
  'data_visualization': 'Data Visualization',
  'dashboard_customization': 'Dashboard Customization',
};

/**
 * Format a feature name to display name
 * @param {string} featureName - Technical feature name (e.g., "multi_subsidiary")
 * @returns {string} - Display name (e.g., "Multi-Subsidiary Management")
 */
export const formatFeatureName = (featureName) => {
  if (!featureName) return '';
  
  // Check if we have a predefined display name
  if (FEATURE_DISPLAY_NAMES[featureName]) {
    return FEATURE_DISPLAY_NAMES[featureName];
  }
  
  // Handle different naming conventions
  let formatted = featureName;
  
  // First, handle snake_case (underscores)
  if (formatted.includes('_')) {
    return formatted
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle camelCase and PascalCase (insert space before capital letters)
  // e.g., "businessPlanList" or "BusinessPlanList" -> "Business Plan List"
  formatted = formatted
    .replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2'); // Handle acronyms like "KPIDashboard" -> "KPI Dashboard"
  
  // Capitalize first letter of each word
  return formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Format multiple feature names
 * @param {Array<string>} features - Array of technical feature names
 * @returns {Array<string>} - Array of display names
 */
export const formatFeatureNames = (features) => {
  if (!Array.isArray(features)) return [];
  return features.map(formatFeatureName);
};

/**
 * Get feature category based on name
 * @param {string} featureName - Technical feature name
 * @returns {string} - Category name
 */
export const getFeatureCategory = (featureName) => {
  if (['basic_planning', 'single_business_plan', 'standard_reports'].includes(featureName)) {
    return 'Basic';
  }
  if (['advanced_planning', 'performance_tracking', 'scenario_modeling', 'advanced_kpis'].includes(featureName)) {
    return 'Advanced';
  }
  if (['multi_subsidiary', 'cost_centers', 'business_intelligence', 'advanced_reports', 'export'].includes(featureName)) {
    return 'Business';
  }
  if (['api_access', 'custom_integrations', 'white_labeling', 'dedicated_support'].includes(featureName)) {
    return 'Enterprise';
  }
  return 'Other';
};

/**
 * Get feature icon based on category
 * @param {string} featureName - Technical feature name  
 * @returns {string} - Icon name for lucide-react
 */
export const getFeatureIcon = (featureName) => {
  const category = getFeatureCategory(featureName);
  const iconMap = {
    'Basic': 'FileText',
    'Advanced': 'TrendingUp',
    'Business': 'Building',
    'Enterprise': 'Crown',
    'Other': 'Check'
  };
  return iconMap[category] || 'Check';
};

export default {
  formatFeatureName,
  formatFeatureNames,
  getFeatureCategory,
  getFeatureIcon,
  FEATURE_DISPLAY_NAMES
};
